/**
 * Canonical registry of the Workbuddy Agent's GATEABLE tools.
 *
 * This is the single source of truth shared by jms-api and jms-web (both import
 * directly from @jms/model) for the per-tool feature-flag grid:
 *   - jms-web renders the grid from this list (label/description/tier per row).
 *   - jms-api computes the per-request enabled-tool set from this list crossed
 *     with the tenant's `ai.assistant.tool.*` feature flags.
 *
 * NOTE on the build boundary: jms-ai is packaged as its own Docker build context
 * and CANNOT import @jms/model. jms-ai therefore keeps its own small set of
 * always-on foundational tool names (FLAG_EXEMPT_TOOL_NAMES in
 * src/mastra/utils/permissions/tool.access.rules.ts) and receives the computed
 * enabled-tool set in the request context. The contract between the two sides is
 * simple: every gateable tool here is NOT in jms-ai's foundational set, and every
 * tool in jms-ai's foundational set is intentionally absent from this registry.
 *
 * Foundational tools (always on, never flagged) are deliberately NOT listed here:
 * code_execution, interview, presentDownloadableFile, restoreSandboxFile,
 * listDurableThreadStorage, listAvailableEntities, getEntityMetadata,
 * queryEntityItems, searchDocs, getDocArticle, listDocs.
 * (The entity-query trio stays foundational — the agent always HAS the read tools. WHICH entities it
 * may read is gated PER-ENTITY via AGENT_ENTITY_REGISTRY, separate from this tool registry.)
 */

/**
 * Rollout maturity for an agent tool. Engineers promote a tool UP the tiers via a
 * code change as it matures; the tier drives the grid grouping and whether a tool
 * is toggleable. It does NOT decide the live on/off state — that is the per-tenant
 * feature flag, which defaults OFF until explicitly enabled.
 */
export enum WorkbuddyAgentToolTier {
    /** Ready for production. Toggleable; recommended on once verified per tenant/environment. */
    EnableNow = 'enable-now',
    /** Work-in-progress / experimental. Toggleable for testing (e.g. vnext) but off by default. */
    Wip = 'wip',
    /** Not ready. Rendered locked-off in the grid; cannot be enabled until promoted in code. */
    DontEnable = 'dont-enable',
}

/** Ordered tiers with display copy, used to render the three grid sections in order. */
export const WORKBUDDY_AGENT_TOOL_TIERS: ReadonlyArray<{ tier: WorkbuddyAgentToolTier; label: string; description: string }> = [
    { tier: WorkbuddyAgentToolTier.EnableNow, label: 'Enable now', description: 'Production-ready tools. Turn these on per tenant when you are ready to roll them out.' },
    { tier: WorkbuddyAgentToolTier.Wip, label: 'WIP & Experimental', description: 'In development. Safe to enable for testing (e.g. on vnext) but not yet recommended for production.' },
    { tier: WorkbuddyAgentToolTier.DontEnable, label: 'Do not enable', description: 'Not ready for use. Locked off until promoted.' },
];

export interface AgentToolRegistryEntry {
    /**
     * Flag key + grid row id. For a single tool this IS the runtime tool name; for a bundle
     * (one flag over several tools, e.g. the form builder) it is a synthetic group key and
     * `gatesToolIds` lists the real tool names it controls.
     */
    toolId: string;
    /** Admin-facing label shown in the feature-flag grid. */
    label: string;
    /** Admin-facing one-line description shown in the feature-flag grid. */
    description: string;
    /** Rollout maturity tier (grouping + lock behaviour). */
    tier: WorkbuddyAgentToolTier;
    /** Read-only capability vs a write/edit one. Read-only entries sort first in each grid tab. Defaults to 'write'. */
    kind?: 'read' | 'write';
    /**
     * The runtime tool names this one flag controls. Defaults to `[toolId]`. Set this when a
     * single grid switch should gate SEVERAL tools as a unit — e.g. the form builder is one
     * switch over its five sub-tools rather than five separate flags.
     */
    gatesToolIds?: string[];
}

/** Dotted feature-flag id prefix for an agent tool sub-flag, nested under `ai.assistant`. */
export const AGENT_TOOL_FLAG_PREFIX = 'ai.assistant.tool.' as const;

/** Build the feature-flag id for a registry entry key (e.g. `createInvoice` -> `ai.assistant.tool.createInvoice`). */
export function agentToolFlagId(toolId: string): string {

    return `${AGENT_TOOL_FLAG_PREFIX}${toolId}`;
}

/** Parse a registry key back out of a feature-flag id, or null if it is not a tool sub-flag. */
export function agentToolIdFromFlagId(flagId: string): string | null {

    return flagId.startsWith(AGENT_TOOL_FLAG_PREFIX) ? flagId.slice(AGENT_TOOL_FLAG_PREFIX.length) : null;
}

/** The runtime tool names a registry entry gates — a single tool by default, or a bundle's members. */
export function agentToolGatedNames(entry: AgentToolRegistryEntry): string[] {

    return entry.gatesToolIds ?? [entry.toolId];
}

/**
 * The gateable tool catalog. Order within a tier is the order shown in the grid.
 * Adding a new gateable tool: add it here (tier `wip` or `dont-enable` until ready)
 * AND register it in jms-ai. A registered jms-ai tool that is neither foundational
 * nor listed here is fail-closed (hidden from the model) until added.
 */
export const AGENT_TOOL_REGISTRY: ReadonlyArray<AgentToolRegistryEntry> = [
    // ===== Enable now =====
    // --- Jobs (write) ---
    { toolId: 'updateJobDescription', label: 'Update Job Description', description: "Change a job's description text.", tier: WorkbuddyAgentToolTier.EnableNow },
    { toolId: 'addJobActivityNote', label: 'Add Job Activity Note', description: "Post a note to a job's activity feed.", tier: WorkbuddyAgentToolTier.EnableNow },
    { toolId: 'renameJobOrStage', label: 'Rename Job or Stage', description: 'Rename a job or one of its stages.', tier: WorkbuddyAgentToolTier.EnableNow },
    { toolId: 'updateJobPriority', label: 'Update Job Priority', description: "Change a job's priority, within tenant caps.", tier: WorkbuddyAgentToolTier.EnableNow },
    { toolId: 'updateJobType', label: 'Update Job Type', description: "Change a job's type.", tier: WorkbuddyAgentToolTier.EnableNow },
    { toolId: 'updateJobStartDate', label: 'Update Job Start Date', description: "Set a job's start date, within tenant limits.", tier: WorkbuddyAgentToolTier.EnableNow },
    { toolId: 'updateJobDueDate', label: 'Update Job Due Date', description: "Set a job's due date, within tenant limits.", tier: WorkbuddyAgentToolTier.EnableNow },
    { toolId: 'updateJobLabels', label: 'Update Job Labels', description: 'Add or remove labels/tags on a job.', tier: WorkbuddyAgentToolTier.EnableNow },
    { toolId: 'updateJobRefs', label: 'Update Job References', description: "Set a job's customer WO#/PO# reference fields.", tier: WorkbuddyAgentToolTier.EnableNow },

    // --- Stages / line items (write) ---
    { toolId: 'addLineItemsToJob', label: 'Add Line Items', description: "Add catalogue line items to a job's stages.", tier: WorkbuddyAgentToolTier.EnableNow },
    { toolId: 'updateStageStartDate', label: 'Update Stage Start Date', description: "Set a stage's start date.", tier: WorkbuddyAgentToolTier.EnableNow },
    { toolId: 'updateStageDueDate', label: 'Update Stage Due Date', description: "Set a stage's due date.", tier: WorkbuddyAgentToolTier.EnableNow },
    { toolId: 'updateStageCategory', label: 'Change Stage Category', description: "Change a stage's work category.", tier: WorkbuddyAgentToolTier.EnableNow },
    { toolId: 'assignStageOwner', label: 'Assign Stage Owner', description: 'Set the owner responsible for a stage.', tier: WorkbuddyAgentToolTier.EnableNow },
    {
        toolId: 'manageStageResources',
        label: 'Manage Stage Resources',
        description: 'Assign or remove internal employee resources and external subcontractors on stages.',
        tier: WorkbuddyAgentToolTier.EnableNow,
        gatesToolIds: [
            'assignSchedulableResource',
            'removeSchedulableResource',
            'assignStageSubcontractor',
            'removeStageSubcontractor',
        ],
    },

    // --- Catalogue (read) ---
    { toolId: 'listCatalogueCategories', label: 'List Catalogue Categories', description: 'Read the SOR catalogue category structure.', tier: WorkbuddyAgentToolTier.EnableNow, kind: 'read' },

    // ===== WIP & Experimental =====
    // --- Stages (write) ---
    { toolId: 'updateStageOrder', label: 'Reorder Stages', description: 'Change the order of stages on a job.', tier: WorkbuddyAgentToolTier.Wip },
    { toolId: 'changeWorkOrderStatus', label: 'Change Stage Status', description: 'Change a stage (work order) status.', tier: WorkbuddyAgentToolTier.Wip },

    // --- Tasks (write) ---
    { toolId: 'createTask', label: 'Create Task', description: 'Create tasks on a job.', tier: WorkbuddyAgentToolTier.Wip },
    { toolId: 'updateTask', label: 'Update Task', description: 'Edit existing tasks.', tier: WorkbuddyAgentToolTier.Wip },
    { toolId: 'completeTask', label: 'Complete Task', description: 'Mark tasks complete.', tier: WorkbuddyAgentToolTier.Wip },
    { toolId: 'deleteTask', label: 'Delete Task', description: 'Delete tasks.', tier: WorkbuddyAgentToolTier.Wip },
    { toolId: 'assignTask', label: 'Assign Task', description: 'Assign or unassign a task.', tier: WorkbuddyAgentToolTier.Wip },

    // --- Invoices (write) ---
    { toolId: 'buildInvoiceDraft', label: 'Build Invoice Draft', description: 'Assemble an invoice draft for review without saving.', tier: WorkbuddyAgentToolTier.Wip },
    { toolId: 'createInvoice', label: 'Create Invoice', description: 'Create an invoice from a job or stage.', tier: WorkbuddyAgentToolTier.Wip },
    { toolId: 'editInvoice', label: 'Edit Invoice', description: 'Edit header fields and lines on an existing invoice.', tier: WorkbuddyAgentToolTier.Wip },
    { toolId: 'processInvoice', label: 'Process Invoice', description: 'Approve/process an invoice.', tier: WorkbuddyAgentToolTier.Wip },
    { toolId: 'syncInvoice', label: 'Sync Invoice', description: 'Sync an invoice to the connected accounting system.', tier: WorkbuddyAgentToolTier.Wip },

    // ===== Do not enable (locked) =====
    { toolId: 'createCrmAccount', label: 'Create CRM Account', description: 'Create customers, suppliers, or contractors. Disabled pending rework.', tier: WorkbuddyAgentToolTier.DontEnable },
    { toolId: 'proposeBillScan', label: 'Scan Bill', description: 'Propose a bill from an uploaded document. Disabled pending rework.', tier: WorkbuddyAgentToolTier.DontEnable },
    // Form builder: ONE switch over all of its sub-tools (not a flag per sub-component).
    { toolId: 'formBuilder', label: 'Form Builder', description: 'Build WorkBuddy .wbf form templates from files and images. Disabled pending rework.', tier: WorkbuddyAgentToolTier.DontEnable, gatesToolIds: ['extractFormSpecFromFiles', 'compileWbfFromSpec', 'validateWbfTemplate', 'scoreWbfSimilarity', 'buildWbfFromFiles'] },
];
